import React, { useState, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./reviewCalculator.css";

const PRESETS = [4.0, 4.2, 4.5, 4.7, 4.9];

const ReviewCalculator = () => {
  const [reviewCount, setReviewCount] = useState("");
  const [currentRating, setCurrentRating] = useState("");
  const [targetRating, setTargetRating] = useState("");
  const calcRef = useRef(null);

  const scrollToCalc = () => {
    calcRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const result = useMemo(() => {
    const count = parseFloat(reviewCount);
    const current = parseFloat(currentRating);
    const target = parseFloat(targetRating);

    if (!count || !current || !target) return null;
    if (count <= 0 || current <= 0 || current > 5) return null;
    if (target <= current) return { needed: 0, alreadyMet: true };
    if (target >= 5) return { needed: Infinity, unreachable: true };

    const needed = Math.ceil(
      (count * (target - current)) / (5 - target)
    );

    const newTotal = count + needed;
    const newRating = ((count * current) + (needed * 5)) / newTotal;

    return { needed, newTotal, newRating: newRating.toFixed(2) };
  }, [reviewCount, currentRating, targetRating]);

  return (
    <div className="calc-page">
      <Helmet>
        <title>Calculateur d'avis manquants Google | Combien d'avis 5 étoiles pour atteindre votre objectif ?</title>
        <meta
          name="description"
          content="Calculez gratuitement combien d'avis 5 étoiles il vous faut pour atteindre votre note cible sur Google. Outil gratuit pour entreprises locales, franchises et agences."
        />
        <link rel="canonical" href="https://localranker.tech/missing-reviews-calculator" />
        <meta property="og:title" content="Calculateur d'avis manquants Google | Localranker" />
        <meta property="og:description" content="Calculez gratuitement combien d'avis 5 étoiles il vous faut pour atteindre votre note cible sur Google." />
        <meta property="og:url" content="https://localranker.tech/missing-reviews-calculator" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>

      <Hero
        title="Calculateur d'avis manquants"
        subtitle="Combien d'avis 5 étoiles vous faut-il pour atteindre votre objectif de note Google ?"
        ctaText="Tester maintenant"
        ctaOnClick={scrollToCalc}
      />

      <div className="calc" ref={calcRef}>
        <div className="calc__card">
          <div className="calc__grid">
            <div className="calc__field">
              <label className="calc__label">Nombre d'avis actuels</label>
              <input
                className="calc__input"
                type="number"
                min="1"
                placeholder="ex: 47"
                value={reviewCount}
                onChange={(e) => setReviewCount(e.target.value)}
              />
            </div>

            <div className="calc__field">
              <label className="calc__label">Note moyenne actuelle</label>
              <input
                className="calc__input"
                type="number"
                min="1"
                max="5"
                step="0.1"
                placeholder="ex: 3.8"
                value={currentRating}
                onChange={(e) => setCurrentRating(e.target.value)}
              />
            </div>

            <div className="calc__field calc__field--full">
              <label className="calc__label">Note cible souhaitée</label>
              <div className="calc__target-row">
                <input
                  className="calc__input"
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  placeholder="ex: 4.5"
                  value={targetRating}
                  onChange={(e) => setTargetRating(e.target.value)}
                />
                <div className="calc__presets">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`calc__preset${parseFloat(targetRating) === p ? " calc__preset--active" : ""}`}
                      onClick={() => setTargetRating(String(p))}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="calc__result">
              {result.alreadyMet ? (
                <div className="calc__result-card calc__result-card--success">
                  <span className="calc__result-icon">✅</span>
                  <div>
                    <p className="calc__result-title">Objectif déjà atteint !</p>
                    <p className="calc__result-desc">
                      Votre note actuelle est déjà égale ou supérieure à votre cible.
                    </p>
                  </div>
                </div>
              ) : result.unreachable ? (
                <div className="calc__result-card calc__result-card--warning">
                  <span className="calc__result-icon">⚠️</span>
                  <div>
                    <p className="calc__result-title">Objectif impossible</p>
                    <p className="calc__result-desc">
                      Une note cible de 5.0 ne peut être atteinte qu'avec uniquement des avis 5 étoiles.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="calc__result-card">
                    <div className="calc__result-number">{result.needed}</div>
                    <div>
                      <p className="calc__result-title">
                        avis 5 étoiles nécessaires
                      </p>
                      <p className="calc__result-desc">
                        pour passer de <strong>{currentRating}</strong> à{" "}
                        <strong>{result.newRating}</strong> avec{" "}
                        <strong>{result.newTotal}</strong> avis au total
                      </p>
                    </div>
                  </div>

                  <div className="calc__breakdown">
                    <div className="calc__stat">
                      <span className="calc__stat-value">⭐ {currentRating}</span>
                      <span className="calc__stat-label">Note actuelle</span>
                    </div>
                    <div className="calc__stat-arrow">→</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">⭐ {result.newRating}</span>
                      <span className="calc__stat-label">Note projetée</span>
                    </div>
                    <div className="calc__stat-arrow">|</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">{reviewCount}</span>
                      <span className="calc__stat-label">Avis actuels</span>
                    </div>
                    <div className="calc__stat-arrow">+</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">{result.needed}</span>
                      <span className="calc__stat-label">Avis à obtenir</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="calc-seo">
        <div className="calc-seo__inner">

          <article className="calc-seo__block">
            <h2>Pourquoi les avis Google sont essentiels pour votre entreprise locale ?</h2>
            <p>
              Les avis Google sont devenus l'un des critères de choix les plus importants pour les consommateurs.
              Votre note moyenne influence directement votre taux de clics dans les résultats Google,
              votre positionnement dans le Local Pack et, in fine, votre chiffre d'affaires.
            </p>
            <div className="calc-seo__stats-row">
              <div className="calc-seo__stat-card">
                <span className="calc-seo__stat-number">87%</span>
                <span className="calc-seo__stat-text">des consommateurs lisent les avis en ligne avant un achat local</span>
              </div>
              <div className="calc-seo__stat-card">
                <span className="calc-seo__stat-number">+35%</span>
                <span className="calc-seo__stat-text">de clics pour les fiches notées 4.5+ vs celles sous 4.0</span>
              </div>
              <div className="calc-seo__stat-card">
                <span className="calc-seo__stat-number">4.5</span>
                <span className="calc-seo__stat-text">note idéale à viser — le sweet spot crédibilité + SEO</span>
              </div>
            </div>
          </article>

          <article className="calc-seo__block">
            <h2>Comment fonctionne le calculateur d'avis manquants ?</h2>
            <p>
              Notre outil utilise une formule mathématique simple pour déterminer le nombre exact d'avis 5 étoiles
              nécessaires pour atteindre votre note cible. Trois étapes suffisent :
            </p>
            <div className="calc-seo__steps">
              <div className="calc-seo__step">
                <span className="calc-seo__step-num">1</span>
                <div>
                  <strong>Nombre d'avis actuels</strong>
                  <p>Le total affiché sur votre fiche Google Business Profile.</p>
                </div>
              </div>
              <div className="calc-seo__step">
                <span className="calc-seo__step-num">2</span>
                <div>
                  <strong>Note moyenne actuelle</strong>
                  <p>Votre note sur 5 telle qu'affichée par Google.</p>
                </div>
              </div>
              <div className="calc-seo__step">
                <span className="calc-seo__step-num">3</span>
                <div>
                  <strong>Note cible souhaitée</strong>
                  <p>La note que vous souhaitez atteindre (par ex. 4.5).</p>
                </div>
              </div>
            </div>
            <p>
              Le calculateur projette ensuite combien de nouveaux avis 5 étoiles il vous faudra obtenir,
              en tenant compte du poids de vos avis existants. Indispensable pour piloter votre stratégie d'e-reputation.
            </p>
          </article>

          <article className="calc-seo__block">
            <h2>Combien d'avis faut-il pour passer de 4.0 à 4.5 sur Google ?</h2>
            <p>
              La réponse dépend de votre volume d'avis existant. Plus vous avez d'avis,
              plus il en faut pour faire bouger la moyenne.
            </p>
            <div className="calc-seo__example-row">
              <div className="calc-seo__example">
                <div className="calc-seo__example-header">10 avis existants</div>
                <div className="calc-seo__example-body">
                  <span className="calc-seo__example-big">~10</span>
                  <span>avis 5 étoiles pour passer de 4.0 à 4.5</span>
                </div>
              </div>
              <div className="calc-seo__example">
                <div className="calc-seo__example-header">50 avis existants</div>
                <div className="calc-seo__example-body">
                  <span className="calc-seo__example-big">~50</span>
                  <span>avis 5 étoiles pour passer de 4.0 à 4.5</span>
                </div>
              </div>
              <div className="calc-seo__example">
                <div className="calc-seo__example-header">200 avis existants</div>
                <div className="calc-seo__example-body">
                  <span className="calc-seo__example-big">~200</span>
                  <span>avis 5 étoiles pour passer de 4.0 à 4.5</span>
                </div>
              </div>
            </div>
            <p>
              C'est pourquoi il est crucial de commencer à collecter des avis le plus tôt possible :
              plus votre base est petite, plus chaque nouvel avis positif a un impact fort sur votre note globale.
            </p>
          </article>

          <article className="calc-seo__block">
            <h2>Quelle est la note Google idéale à viser ?</h2>
            <div className="calc-seo__scale">
              <div className="calc-seo__scale-zone calc-seo__scale-zone--bad">
                <span className="calc-seo__scale-range">&lt; 4.0</span>
                <span className="calc-seo__scale-label">Insuffisant</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--ok">
                <span className="calc-seo__scale-range">4.0 — 4.2</span>
                <span className="calc-seo__scale-label">Correct</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--good">
                <span className="calc-seo__scale-range">4.3 — 4.7</span>
                <span className="calc-seo__scale-label">Idéal</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--sus">
                <span className="calc-seo__scale-range">4.8 — 5.0</span>
                <span className="calc-seo__scale-label">Suspect</span>
              </div>
            </div>
            <p>
              Une note en dessous de 4.0 décourage une grande partie des prospects.
              À l'inverse, une note parfaite de 5.0 peut paraître suspecte.
              Google utilise la note moyenne comme signal de classement dans le Local Pack — les fiches
              au-dessus de 4.0 sont systématiquement favorisées. Visez <strong>4.5 comme objectif réaliste</strong>.
            </p>
          </article>

          <article className="calc-seo__block">
            <h2>Comment obtenir plus d'avis 5 étoiles sur Google ?</h2>
            <p>
              Obtenir des avis positifs ne relève pas du hasard. Voici les bonnes pratiques
              des entreprises locales les plus performantes :
            </p>
            <div className="calc-seo__tips">
              <div className="calc-seo__tip">
                <span className="calc-seo__tip-icon">📩</span>
                <div>
                  <strong>Demandez systématiquement</strong>
                  <p>Après chaque interaction client réussie, envoyez un SMS ou un email avec un lien direct vers votre fiche Google.</p>
                </div>
              </div>
              <div className="calc-seo__tip">
                <span className="calc-seo__tip-icon">🔗</span>
                <div>
                  <strong>Facilitez le parcours</strong>
                  <p>Utilisez un lien court ou un QR code qui mène directement au formulaire d'avis Google.</p>
                </div>
              </div>
              <div className="calc-seo__tip">
                <span className="calc-seo__tip-icon">💬</span>
                <div>
                  <strong>Répondez à tous les avis</strong>
                  <p>Les avis positifs comme négatifs. Les clients qui voient des réponses sont plus enclins à laisser le leur.</p>
                </div>
              </div>
              <div className="calc-seo__tip">
                <span className="calc-seo__tip-icon">👥</span>
                <div>
                  <strong>Formez vos équipes</strong>
                  <p>Chaque collaborateur en contact client doit savoir quand et comment demander un avis.</p>
                </div>
              </div>
              <div className="calc-seo__tip">
                <span className="calc-seo__tip-icon">⚡</span>
                <div>
                  <strong>Automatisez la collecte</strong>
                  <p>Des outils comme LocalRanker permettent d'envoyer des demandes d'avis automatiques après chaque visite ou achat.</p>
                </div>
              </div>
            </div>
          </article>

          <article className="calc-seo__block">
            <h2>Les avis Google impactent-ils le référencement local (SEO local) ?</h2>
            <p>
              Oui, de manière significative. Google a confirmé que les avis sont l'un des principaux
              facteurs de classement dans les résultats locaux. Trois signaux sont pris en compte :
            </p>
            <div className="calc-seo__factors">
              <div className="calc-seo__factor">
                <span className="calc-seo__factor-icon">📈</span>
                <strong>Volume</strong>
                <p>Plus vous avez d'avis, plus Google considère votre fiche comme légitime et populaire.</p>
              </div>
              <div className="calc-seo__factor">
                <span className="calc-seo__factor-icon">⭐</span>
                <strong>Note moyenne</strong>
                <p>Une note élevée est un signal de qualité qui améliore votre positionnement.</p>
              </div>
              <div className="calc-seo__factor">
                <span className="calc-seo__factor-icon">🕐</span>
                <strong>Fraîcheur</strong>
                <p>Des avis récents et réguliers montrent que votre entreprise est active.</p>
              </div>
            </div>
            <p>
              En travaillant activement sur votre collecte d'avis, vous améliorez à la fois votre
              image de marque et votre visibilité dans Google Maps et le Local Pack.
            </p>
          </article>

        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default ReviewCalculator;
